import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { VatAmountCalculator } from '@zaoblako/shared/common/classes/vat.amount.calculator';
import { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { ApprovalSchemeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-find.dto';
import { ApprovalSchemeQueryInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-query.dto';
import type { BankFindInterface } from '@zaoblako/shared/modules/catalog/dtos/bank/bank-find.dto';
import { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import { DirectionKindEnum } from '@zaoblako/shared/modules/catalog/enums/direction.kind.enum';
import { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import { NdsFindInterface } from '@zaoblako/shared/modules/finance/dtos/nds-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { TaskStatusFindInterface } from '@zaoblako/shared/modules/task/dtos/status/task-status-find.dto';
import type { TaskFindInterface } from '@zaoblako/shared/modules/task/dtos/task-find.dto';
import type { TaskFormInterface, TaskFormOperation } from '@zaoblako/shared/modules/task/dtos/task-form.dto';
import { TaskPriorityFindInterface } from '@zaoblako/shared/modules/task/dtos/task-priority-find.dto';
import { TaskKindEnum } from '@zaoblako/shared/modules/task/enum/task-kind.enum';
import { TaskPaymentTypeEnum } from '@zaoblako/shared/modules/task/enum/task.payment.type.enum';
import { TaskStatusTypeEnum } from '@zaoblako/shared/modules/task/enum/task.status.type.enum';
import { ApprovalSchemeService } from '@zaoblako/web/core/services/budget/approval-scheme.service';
import { BankService } from '@zaoblako/web/core/services/catalog/bank.service';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { ContragentService } from '@zaoblako/web/core/services/client/contragent.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { DealService } from '@zaoblako/web/core/services/deal/deal.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { FilialService } from '@zaoblako/web/core/services/finance/filial.service';
import { NdsService } from '@zaoblako/web/core/services/finance/nds.service';
import { UnitService } from '@zaoblako/web/core/services/finance/unit.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { TaskPriorityService } from '@zaoblako/web/core/services/task/task-priority.service';
import { TaskStatusService } from '@zaoblako/web/core/services/task/task-status.service';
import { TaskTemplateService } from '@zaoblako/web/core/services/task/task-template.service';
import { TaskService } from '@zaoblako/web/core/services/task/task.service';
import { TaskEditPermissions } from '@zaoblako/web/core/types/task-edit-permissions.type';
import { TaskFormComponent } from '@zaoblako/web/modules/payments/dumbs/task-form/task-form.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { cloneDeep } from 'lodash';
import * as moment from 'moment-timezone';
import { forkJoin, Observable, switchMap, tap } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-task-form-sidebar',
  templateUrl: './task-form-sidebar.component.html',
  imports: [CommonModule, TaskFormComponent, SpinnerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskFormSidebarComponent implements OnInit {
  @Input() public readonly index: number;
  public task = <TaskFormInterface>{};
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public activeStep = 0;

  public organizations: CompanyFindInterface[] = [];
  public filials: FilialFindInterface[] = [];
  public units: UnitFindInterface[] = [];
  public directions: DirectionFindInterface[] = [];
  public priorities: TaskPriorityFindInterface[] = [];
  public nds: NdsFindInterface[] = [];
  public banks: BankFindInterface[] = [];
  public users: UserFindInterface[] = [];
  public userGroups: UserGroupFindInterface[] = [];
  public statuses: TaskStatusFindInterface[] = [];
  public schemes: ApprovalSchemeFindInterface[] = [];
  public currencies: CurrencyFindInterface[] = [];

  public operation: TaskFormOperation = 'create';

  public taskEditPermissions: TaskEditPermissions;

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly taskService: TaskService,
    private readonly taskStatusService: TaskStatusService,
    private readonly companyService: CompanyService,
    private readonly filialService: FilialService,
    private readonly unitService: UnitService,
    private readonly directionService: DirectionService,
    private readonly taskPriorityService: TaskPriorityService,
    private readonly ndsService: NdsService,
    private readonly bankService: BankService,
    private readonly userService: UserService,
    private readonly userGroupService: UserGroupService,
    private readonly errorService: ErrorService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly taskTemplateService: TaskTemplateService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly approvalSchemeService: ApprovalSchemeService,
    private readonly dealService: DealService,
    private readonly contragentService: ContragentService,
    private readonly confirmDialogService: ConfirmDialogService
  ) {}

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    const copyId = this.formStoreService.getState.forms[this.index]?.data?.copyId;
    const templateId = this.formStoreService.getState.forms[this.index]?.data?.templateId;
    const dealId = this.formStoreService.getState.forms[this.index]?.data?.dealId;

    if (templateId || copyId) {
      this.formStoreService.setLoading(true, this.index);
    }

    if (copyId) {
      this.taskService
        .findById<TaskFindInterface>(copyId)
        .pipe(tap(() => this.formStoreService.setLoading(false, this.index)))
        .subscribe((task) => {
          this.task = task as unknown as TaskFormInterface;
          this.task.status = null;
          if (isNaN(this.task.vat)) {
            this.task.vat = 0;
          }
          if (this.task.nds?.amount !== null && this.task.nds?.amount !== undefined) {
            this.task.vat = VatAmountCalculator.getInstance().getVatAmount(
              this.task.amount || 0,
              this.task.nds?.amount || 0
            );
          }
          this.cdRef.markForCheck();
        });
    }

    if (templateId) {
      this.taskTemplateService
        .getTaskFormByTemplate(templateId)
        .pipe(tap(() => this.formStoreService.setLoading(false, this.index)))
        .subscribe((data) => {
          this.task = data.task;
          if (isNaN(this.task.vat)) {
            this.task.vat = 0;
          }
          this.activeStep = 1;
          this.cdRef.markForCheck();
        });
    }

    if (!isNaN(dealId)) {
      this.dealService
        .findById<DealFindInterface>(dealId)
        .pipe(
          tap((deal) => {
            const dealFields: Array<keyof DealFindInterface> = ['amount', 'filial', 'direction', 'unit'];
            dealFields.forEach((key) => {
              this.task[key] = cloneDeep(deal[key]);
            });
            this.task.deal = deal;
            this.cdRef.markForCheck();
          }),
          switchMap((deal) => this.contragentService.search({ search: deal.contragent.name, withBankDetails: true }))
        )
        .subscribe((contragents) => {
          this.task.contragent = contragents[0];
          this.task = { ...this.task };
          this.cdRef.markForCheck();
        });
    }

    forkJoin([
      this.userService.findAll(),
      this.userGroupService.find<UserGroupFindInterface>(),
      this.taskStatusService.find<TaskStatusFindInterface>(),
      this.directionService.find<DirectionFindInterface>({
        kinds: [
          DirectionKindEnum.OPERATING_EXPENSE,
          DirectionKindEnum.OTHER_EXPENSE,
          DirectionKindEnum.COMMERCIAL_EXPENSE,
          DirectionKindEnum.FINANCIAL_ACTIVITY,
          DirectionKindEnum.TECHNICAL_OPERATIONS
        ]
      })
    ]).subscribe(([users, userGroups, statuses, directions]) => {
      this.directions = directions;
      this.users = users;
      this.userGroups = userGroups;
      this.statuses = statuses.filter((status) =>
        [TaskStatusTypeEnum.DRAFT, TaskStatusTypeEnum.SCHEDULED, TaskStatusTypeEnum.ON_APPROVAL].includes(status.type)
      );

      if (!this.task.kind && !this.formStoreService.getState.forms[this.index]?.isEdit) {
        this.task.kind = TaskKindEnum.PAYMENT_TO_SUPPLIER;
      }
      this.task = { ...this.task };
      this.cdRef.markForCheck();
    });

    forkJoin([
      this.companyService.find<CompanyFindInterface>(),
      this.filialService.find<FilialFindInterface>(),
      this.unitService.find<UnitFindInterface>(),
      this.taskPriorityService.find<TaskPriorityFindInterface>(),
      this.ndsService.find<NdsFindInterface>(),
      this.approvalSchemeService.find<ApprovalSchemeFindInterface>(<ApprovalSchemeQueryInterface>{
        onlyWithExplicitAccess: true
      })
    ]).subscribe(([organizations, filials, units, priorities, nds, schemes]) => {
      this.organizations = organizations;
      this.filials = filials;
      this.units = units;
      this.priorities = priorities;
      this.nds = nds;
      this.schemes = schemes;

      if (this.task.filial) {
        this.task.filial = this.filials?.find((filial) => filial?.id === this.task.filial?.id);
      }

      if (!this.task.scheme) {
        this.task.scheme = this.schemes?.find((scheme) => scheme.isDefault);
        this.setDefaultUsersAndGroups();
      } else {
        this.task.scheme = this.schemes?.find((scheme) => this.task.scheme?.id === scheme?.id);
      }

      if (!this.formStoreService.getState.forms[this.index]?.isEdit) {
        if (!this.task.vat) {
          this.task.vat = 0;
        }
        if (!this.task.priority) {
          this.task.priority = this.priorities.find((priority) => priority.name === 'Средний');
        }
        if (!this.task.type) {
          this.task.type = TaskPaymentTypeEnum.BANK;
        }
        if (!this.task.organization && this.organizations.length === 1) {
          this.task.organization = this.organizations[0];
        }
        if (!this.task.unit && this.units.length === 1) {
          this.task.unit = this.units[0];
        }
        if (!this.task.filial && this.filials.length === 1) {
          this.task.filial = this.filials[0];
        }
        if (!this.task.date || isNaN(this.task.date.getTime())) {
          this.task.date = moment().toDate();
        }
        if (!this.task.subject) {
          this.task.subject = 'Оплата по счет №.  от ';
        }
        if (!this.task.responsible) {
          this.task.responsible = this.authService.currentUser;
          if (this.task.responsible?.approvalScheme && !this.task.scheme) {
            this.task.scheme = this.task.responsible.approvalScheme;
          }
        }
        if (this.task.orders?.length) {
          this.task.orders.forEach((order) => {
            if (isNaN(order.date.getTime())) {
              order.date = moment().toDate();
            }
          });
        }
      }
      this.task = { ...this.task };
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.formStoreService.setLoading(true, this.index);

      forkJoin([
        this.taskService.findById<TaskFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId),
        this.approvalSchemeService.find<ApprovalSchemeFindInterface>()
      ]).subscribe(([task, schemes]) => {
        this.operation = 'update';
        this.task = task;
        this.task.scheme = schemes?.find((scheme) => this.task.scheme?.id === scheme?.id);
        if (this.task.organization?.currencies?.length) {
          this.currencies = this.task.organization.currencies;
        }
        const { permissions } = this.taskService.getTaskPermissions(task);
        if (!task.canEdit) {
          this.formStoreService.closeSidebar(this.index);
          this.router.navigate(['/payments/task/card/', task.id]).catch(console.error);
        } else {
          this.taskEditPermissions = permissions;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
    }

    this.taskEditPermissions = this.taskService.getTaskPermissions(
      this.task as unknown as TaskFindInterface
    ).permissions;
    Object.keys(this.taskEditPermissions).forEach((prop) => (this.taskEditPermissions[prop] = true));
  }

  public searchBank(name: string) {
    this.bankService.search({ search: name }).subscribe((banks) => {
      this.banks = banks;
      this.cdRef.markForCheck();
    });
  }

  public saveTask(task: TaskFormInterface) {
    if (this.formStoreService.getState.forms[this.index]?.entityId) {
      const savingMethod = () => {
        this.formStoreService.setLoading(true, this.index);

        this.taskService.update(task).subscribe({
          next: (newTask) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(
              `Заявка №${this.formStoreService.getState.forms[this.index]?.entityId} обновлена`
            );
            this.formStoreService.saveNext(newTask, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      };

      if (task.amount !== task.orders?.reduce((sum, order) => order.amount, 0)) {
        this.confirmDialogService.confirm({
          message: 'Сумма заявки и сумма платежных поручений отличаются друг от друга.<br />Продолжить сохранение?',
          accept: () => {
            savingMethod();
          }
        });
        return;
      }

      savingMethod();
    } else {
      this.formStoreService.setLoading(true, this.index);

      this.taskService.createTask(task).subscribe({
        next: (newTask) => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage('Заявка создана');
          if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
            this.formStoreService.closeSidebar(this.index);
            this.router.navigate(['/payments/task/card/' + newTask.id]).catch(console.error);
          } else {
            this.formStoreService.saveNext(newTask, this.index);
          }
        },
        error: () => {
          this.activeStep = 1;
          this.formStoreService.setLoading(false, this.index);
        }
      });
    }
  }

  public deleteTask() {
    this.confirmDialogService.confirm({
      message: `Вы действительно хотите удалить заявку №${this.formStoreService.getState.forms[this.index]?.entityId}?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);

        this.taskService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe(() => {
          this.errorService.addSuccessMessage(
            `Заявка №${this.formStoreService.getState.forms[this.index]?.entityId} удалена`
          );
          this.formStoreService.deleteNext(this.index);
        });
      }
    });
  }

  private setDefaultUsersAndGroups() {
    if (this.task.scheme) {
      if (this.task.scheme.userAuditors?.length) {
        this.task.userAuditors = this.task.scheme.userAuditors;
      }
      if (this.task.scheme?.userGroupAuditors?.length) {
        this.task.userGroupAuditors = this.task.scheme.userGroupAuditors;
      }
    }
  }

  public changeOrganization() {
    this.currencies = [];
    this.task.currency = null;

    if (this.task.organization?.currencies?.length) {
      this.currencies = this.task.organization.currencies;
      setTimeout(() => {
        this.task.currency = this.currencies.find((currency) => currency.isDefault);
        this.task['rate'] = this.task.currency?.latestRate?.rate;
        this.cdRef.markForCheck();
      });
    }
  }
}
